import { Banner, Icon, IconButton, Text } from "@fidelix/fx-miranda";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { styled } from "styled-components";
import { useEffect, useState } from "react";
import { DeviceLite, DeviceStatus } from "./device.model";
import { useDeviceStateContext } from "./device-status-context";

type Props = {
  device?: DeviceLite;
};
export function DeviceErrorBanner({ device }: Props) {
  const { getDeviceState, isLoading, getDevice } = useDeviceStateContext();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (device && !isLoading) {
      const deviceState = getDevice(device.id);
      // This is done to avoid the annoying flicker that can happen if the device
      // state hasn't actually loaded and we get back the fallback device info
      if (!deviceState.lastOnlineAt && !deviceState.version) {
        return;
      }
      setShow(deviceState.status !== DeviceStatus.Connected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getDeviceState, device]);

  useEffect(() => {
    // Trigger a resize event when this banner is closed
    // This is done so all our floating elements adjust and resize properly when the banner disappears
    return () => {
      window.dispatchEvent(new Event("resize"));
    };
  }, [show]);

  if (!show) {
    return null;
  }

  const errorText =
    device && getDeviceState(device.id) === DeviceStatus.Disabled
      ? t`The device is disabled and communication with the cloud has been interrupted.`
      : t`The device is currently offline and has entered an error state.`;

  return (
    <BannerContainer>
      <IconContainer>
        <Icon icon="attentionFilled" color="mutedDarkNeutral" />
      </IconContainer>
      <TextContainer>
        <Text variant="bodyBold">
          <Trans>Device connection error</Trans>
        </Text>
        <Text variant="body">{errorText}</Text>
      </TextContainer>
      <IconButton
        variant="transparent"
        shape="round"
        icon="close"
        onPress={() => setShow(false)}
      />
    </BannerContainer>
  );
}

const BannerContainer = styled(Banner)`
  background: ${(p) => p.theme.colors.mutedLightNeutral};
  border-left: 6px solid ${(p) => p.theme.colors.mutedDarkNeutral};
  position: static;
  display: flex;
  flex-direction: row;
  gap: ${(p) => p.theme.space("small")};
  padding: ${(p) => p.theme.space("normal")} ${(p) => p.theme.space("medium")};
`;

const TextContainer = styled.div`
  width: 95%;
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.space("xsmall")};
`;
const IconContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;
